import React, { useEffect, useState } from 'react';
import { AlertType } from '../stores/data/types';
import { feManagementSystemApi } from '../api/fe-management-system-api';

interface TokenAlertListProps {
  tokenAddress: string;
  maxHeight?: string;
}

interface TokenAlert {
  description: string;
  type: AlertType;
  lastUpdated: Date;
}

const TokenAlertList: React.FC<TokenAlertListProps> = ({
  tokenAddress,
  maxHeight = 'max-h-[200px]',
}) => {
  const [alerts, setAlerts] = useState<TokenAlert[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchAlerts = async () => {
      if (!tokenAddress) return;

      setIsLoading(true);
      setError(null);

      try {
        const alertsData = await feManagementSystemApi.getTokenAlerts(tokenAddress);
        if (alertsData && Array.isArray(alertsData)) {
          const formattedAlerts = alertsData.map((alert: any) => ({
            description: alert.description || '',
            type: alert.type as AlertType,
            lastUpdated: new Date(alert.created_at || alert.updated_at || 0),
          }));
          setAlerts(formattedAlerts);
        } else {
          setAlerts([]);
        }
      } catch (err: any) {
        console.error('❌ Token Alert 조회 실패:', err);
        setError('알림을 불러오는데 실패했습니다.');
        setAlerts([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchAlerts();
  }, [tokenAddress]);

  const getAlertBgColor = (alertType: AlertType): string => {
    switch (alertType) {
      case AlertType.AF_RANK:
        return '#3b82f6'; // blue-500
      case AlertType.TWEET_COUNT:
      case AlertType.HIGH_VALUE_TARGET_MENTION:
        return '#10b981'; // emerald-500
      case AlertType.DEXSCREENER_CTO:
      case AlertType.DEXSCREENER_BOOST:
        return '#8b5cf6'; // violet-500
      default:
        return '#6b7280'; // gray-500
    }
  };

  const formatTimeAgo = (date: Date): string => {
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) {
      return '방금 전';
    } else if (diffMins < 60) {
      return `${diffMins}분 전`;
    } else if (diffHours < 24) {
      return `${diffHours}시간 전`;
    } else {
      return `${diffDays}일 전`;
    }
  };

  if (isLoading) {
    return (
      <div className="bg-gray-800 rounded-lg p-3">
        <h3 className="text-xs font-semibold text-white mb-3">Token Alerts</h3>
        <div className="flex items-center justify-center py-4">
          <p className="text-gray-400 text-xs">Loading...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-gray-800 rounded-lg p-3">
        <h3 className="text-xs font-semibold text-white mb-3">Token Alerts</h3>
        <div className="flex items-center justify-center py-4">
          <p className="text-red-400 text-xs">{error}</p>
        </div>
      </div>
    );
  }

  if (alerts.length === 0) {
    return (
      <div className="bg-gray-800 rounded-lg p-3">
        <h3 className="text-xs font-semibold text-white mb-3">Token Alerts</h3>
        <div className="flex items-center justify-center py-4">
          <p className="text-gray-400 text-xs">No notifications</p>
        </div>
      </div>
    );
  }

  const sortedAlerts = [...alerts].sort(
    (a, b) => b.lastUpdated.getTime() - a.lastUpdated.getTime()
  );

  return (
    <div className="bg-gray-800 rounded-lg p-3">
      <h3 className="text-xs font-semibold text-white mb-3">Token Alerts</h3>
      <div className={`space-y-2 ${maxHeight} overflow-y-auto`}>
        {sortedAlerts.map((alert, index) => {
          const bgColor = getAlertBgColor(alert.type);
          const timeAgo = formatTimeAgo(alert.lastUpdated);

          return (
            <div
              key={`${alert.type}-${index}-${alert.lastUpdated.getTime()}`}
              className="rounded p-2 text-xs"
              style={{ backgroundColor: bgColor }}
            >
              <div className="flex items-start justify-between gap-2">
                <span className="text-white font-medium flex-1">
                  {alert.description}
                </span>
                <span className="text-white/70 text-[10px] whitespace-nowrap">
                  {timeAgo}
                </span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default TokenAlertList;

